import { computed, linkedSignal, Signal } from '@angular/core';
import { CreateQueryResult } from '@tanstack/angular-query-experimental';

import { injectListBudgetAttributesQuery } from '@models/trial-attributes/list-budget-attributes-query';

import { ExtendableDropdownOption } from '@shared/components/extendable-options-dropdown/extendable-options-dropdown.component';
import { listBudgetAttributesQuery } from '@shared/services/gql.service';
import { getEncodedAttributeName } from '@shared/utils/get-encoded-attribute-name';

import { BalanceSheetAccount } from '../components/balance-sheet-account-grid/grid-config/grid-config-context';

export interface BalanceSheetAttributesContext {
  attributeOptions: Signal<Record<string, ExtendableDropdownOption[]>>;
  budgetAttributesQuery: CreateQueryResult<listBudgetAttributesQuery[] | null, Error>;
  getAttributeOptions: (attributeName: string) => Signal<ExtendableDropdownOption[]>;
  onAddAttributeOption: (attributeName: string, option: ExtendableDropdownOption) => void;
}

export function useBalanceSheetAttributesContext({
  originalRowData,
}: {
  originalRowData: Signal<BalanceSheetAccount[]>;
}) {
  // Fetch budget attributes
  const budgetAttributesQuery = injectListBudgetAttributesQuery();

  // Map attribute_name to ExtendableDropdownOption[] as a linkedSignal
  const attributeOptions = linkedSignal(() => {
    const data = budgetAttributesQuery.data();
    const rowData = originalRowData();

    const options: Record<string, ExtendableDropdownOption[]> = {};

    if (!data) return options;
    for (const attr of data) {
      const rowDataOptions = rowData
        .map((row) => row.attributes[getEncodedAttributeName(attr.attribute_name)])
        .filter((o) => o);

      const allOptions = new Set([...(attr.attribute_values || []), ...rowDataOptions]);

      options[attr.attribute_name] = Array.from(allOptions).map((value) => ({
        id: value,
        value,
      }));
    }

    return options;
  });

  // Add a new option to an attribute
  const onAddAttributeOption = (attributeName: string, option: ExtendableDropdownOption) => {
    const current = attributeOptions()[attributeName] || [];

    // Avoid duplicates by value
    if (!current.some((o) => o.value === option.value)) {
      attributeOptions.set({
        ...attributeOptions(),
        [attributeName]: [...current, option],
      });
    }
  };

  // Return a signal for a specific attribute's options
  const getAttributeOptions = (attributeName: string) =>
    computed(() => attributeOptions()[attributeName] || []);

  return {
    attributeOptions,
    budgetAttributesQuery,
    getAttributeOptions,
    onAddAttributeOption,
  };
}
